import { clients } from '@/lib/https';

const CAMPAIGN_API_BASE_URL = import.meta.env.VITE_CAMPAIGN_API_BASE_URL?.replace(/\/$/, '') ?? '';
const inflightGets = new Map<string, Promise<unknown>>();

export type CampaignApiQueryValue = string | number | ReadonlyArray<string | number> | undefined;

export const getCampaignApiUrl = (path: string): string => {
  if (!CAMPAIGN_API_BASE_URL) {
    throw new Error('VITE_CAMPAIGN_API_BASE_URL is not configured');
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  if (CAMPAIGN_API_BASE_URL.startsWith('http')) {
    return `${CAMPAIGN_API_BASE_URL}${normalizedPath}`;
  }

  const basePath = CAMPAIGN_API_BASE_URL.startsWith('/')
    ? CAMPAIGN_API_BASE_URL
    : `/${CAMPAIGN_API_BASE_URL}`;

  return `${window.location.origin}${basePath}${normalizedPath}`;
};

const buildQueryString = (query: Record<string, CampaignApiQueryValue>): string => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === '') continue;
    if (Array.isArray(value)) {
      // Guide §3: repeat params to OR statuses — never comma-join.
      for (const entry of value) {
        if (entry === undefined || entry === '') continue;
        params.append(key, String(entry));
      }
      continue;
    }
    params.set(key, String(value));
  }
  return params.toString();
};

export const campaignApiGet = async <T>(
  path: string,
  query: Record<string, CampaignApiQueryValue> = {}
): Promise<T> => {
  const queryString = buildQueryString(query);
  const url = queryString ? `${getCampaignApiUrl(path)}?${queryString}` : getCampaignApiUrl(path);

  const inflight = inflightGets.get(url);
  if (inflight) {
    return inflight as Promise<T>;
  }

  const request = clients.get<T>(url).finally(() => {
    inflightGets.delete(url);
  });
  inflightGets.set(url, request);
  return request;
};

export const campaignApiPost = async <T>(
  path: string,
  body: unknown,
  headers: HeadersInit = {}
): Promise<T> => {
  const url = getCampaignApiUrl(path);
  return clients.post<T>(url, JSON.stringify(body ?? {}), headers);
};

export const campaignApiPostForm = async <T>(
  path: string,
  formData: FormData,
  headers: HeadersInit = {}
): Promise<T> => {
  const url = getCampaignApiUrl(path);
  return clients.post<T>(url, formData, headers);
};

export const campaignApiGetBlob = async (path: string): Promise<Blob> => {
  const url = getCampaignApiUrl(path);
  return clients.getBlob(url);
};

export const campaignApiGetBlobDetail = async (
  path: string
): Promise<{ blob: Blob; contentDisposition: string | null }> => {
  const url = getCampaignApiUrl(path);
  return clients.getBlobDetail(url);
};

export const campaignApiPut = async <T>(path: string, body: unknown): Promise<T> => {
  const url = getCampaignApiUrl(path);
  return clients.put<T>(url, JSON.stringify(body));
};

export const campaignApiPatch = async <T>(path: string, body: unknown): Promise<T> => {
  const url = getCampaignApiUrl(path);
  return clients.patch<T>(url, JSON.stringify(body));
};

export const campaignApiDelete = async <T>(
  path: string,
  body?: unknown,
  headers: HeadersInit = {}
): Promise<T> => {
  const url = getCampaignApiUrl(path);
  if (body === undefined) {
    return clients.delete<T>(url, headers);
  }
  return clients.delete<T>(url, headers, JSON.stringify(body));
};
